import type {
  SocieteReward,
  SocieteRewardAdminAction,
  SocieteRewardCopayInput,
  SocieteRewardCopayPrice,
} from '~/services/LuxLoyalty';

import { isSocieteRewardEligible } from './isSocieteRewardEligible';
import { parseOverriddenFields } from './parseOverriddenFields';

/**
 * String-backed form state for the Société reward override dialog. Every numeric field is kept
 * as a string so an input can be legitimately empty (meaning "untouched" — leave the live value
 * as-is) rather than coerced to 0, and so a user can type invalid values (e.g. "12.5") that are
 * caught by validation instead of silently clamped.
 */
export type OverrideFormState = {
  /** Base points override. Empty string = untouched. */
  pointsBase: string;
  /** Extra-night points override. Empty string = untouched. */
  pointsExtraNight: string;
  /** One entry per selling currency the package supports. Empty base/extraNight = that field is untouched. */
  copay: Record<string, { base: string; extraNight: string }>;
  /** `null` = eligibility not overridden (leave the computed/auto eligibility as-is). */
  eligible: boolean | null;
  /** Reason for the override, required whenever any other field changed. */
  reason: string;
};

type OverrideAction = Extract<SocieteRewardAdminAction, { action: 'override' }>;

/** Result of {@link buildSocieteRewardAction}: either the action to submit, or a user-facing validation error. */
export type BuildSocieteRewardActionResult = { action: OverrideAction } | { error: string };

export const NOTHING_CHANGED_ERROR = 'Change at least one of points, co-pay or eligibility';
export const REASON_REQUIRED_ERROR = 'Reason is required';
export const INVALID_NUMBER_ERROR = 'Points and co-pay must be whole non-negative numbers';
export const VALUES_REQUIRE_ELIGIBILITY_ERROR =
  'Choose Eligible or Ineligible — an overridden package is no longer auto-calculated';

/** True for a trimmed string that is a whole, non-negative number ("0", "120" — not "12.5", "-1", ""). */
function isWholeNonNegative(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

/** Parses a non-empty numeric form string to a number. Callers only use this once the string has passed validation or when building the "changed" diff (where an invalid string just needs to compare unequal to the live value). */
function toNumber(value: string): number {
  return Number(value.trim());
}

/** The points/co-pay fields `diffValueFields` found to differ from the package's live values. */
export interface ValueFieldsDiff {
  pointsBaseChanged: boolean;
  pointsExtraNightChanged: boolean;
  copayEntries: Array<SocieteRewardCopayInput>;
}

/**
 * Diffs the form's points/co-pay inputs against the package's current live values — the fields
 * that make a package "overridden" in the Rule 2 sense (as opposed to eligibility, which can be
 * overridden on its own without touching any value field). Shared by `buildSocieteRewardAction`
 * (to build the payload) and the override dialog (to drive the dynamic eligibility section — see
 * `eligibilitySectionState`'s `valuesEdited` input).
 */
export function diffValueFields(
  form: OverrideFormState,
  current: { reward: SocieteReward | null; prices: Array<SocieteRewardCopayPrice> },
): ValueFieldsDiff {
  const livePointsBase = current.reward?.points_base ?? 0;
  // Extra-night rates keep a live null as null (no nightly price): coercing it to 0 would make an
  // admin typing 0 — a deliberately free extra night — look like "no change".
  const livePointsExtraNight = current.reward?.points_extra_night ?? null;

  const pointsBaseChanged = form.pointsBase.trim() !== '' && toNumber(form.pointsBase) !== livePointsBase;
  const pointsExtraNightChanged =
    form.pointsExtraNight.trim() !== '' && toNumber(form.pointsExtraNight) !== livePointsExtraNight;

  const copayEntries: Array<SocieteRewardCopayInput> = [];
  for (const [currencyCode, values] of Object.entries(form.copay)) {
    const livePrice = current.prices.find((price) => price.currency_code === currencyCode);
    const liveBase = livePrice?.societe_reward_copay_base ?? 0;
    const liveExtraNight = livePrice?.societe_reward_copay_extra_night ?? null;

    const baseChanged = values.base.trim() !== '' && toNumber(values.base) !== liveBase;
    const extraNightChanged = values.extraNight.trim() !== '' && toNumber(values.extraNight) !== liveExtraNight;
    if (!baseChanged && !extraNightChanged) {
      continue;
    }

    copayEntries.push({
      currency_code: currencyCode,
      // copay_base is required by the API even when only the extra-night copay changed, so fall
      // back to the live value rather than omitting it.
      copay_base: baseChanged ? toNumber(values.base) : liveBase,
      ...(extraNightChanged ? { copay_extra_night: toNumber(values.extraNight) } : {}),
    });
  }

  return { pointsBaseChanged, pointsExtraNightChanged, copayEntries };
}

/**
 * Builds the override form pre-filled with the package's current live values — one row per
 * selling currency, seeded from the matching price row (empty strings where the currency has no
 * price row yet, or where a specific value, e.g. `copay_extra_night`, is `null`).
 *
 * `eligible` starts as `null` ("Auto" — no eligibility override) UNLESS `override_meta.fields`
 * marks eligibility as currently overridden, in which case it's preselected from the reward's
 * live status via `isSocieteRewardEligible` (true for `AUTO_ELIGIBLE`/`ADMIN_ELIGIBLE`, false
 * otherwise) so the toggle reflects the override already in effect instead of defaulting back to
 * "Auto".
 *
 * Used to seed the dialog's form state whenever it opens, so the admin sees (and can resubmit)
 * the package's actual current points/co-pay/eligibility instead of blank inputs. Builder
 * semantics are unaffected: a prefilled value that's resubmitted unedited still equals the live
 * value/status, so it's excluded from the payload — only real edits are sent.
 */
export function buildInitialOverrideForm(
  reward: SocieteReward,
  prices: Array<SocieteRewardCopayPrice>,
  sellingCurrencies: Array<string>,
): OverrideFormState {
  const copay: OverrideFormState['copay'] = {};
  for (const currencyCode of sellingCurrencies) {
    const price = prices.find((p) => p.currency_code === currencyCode);
    copay[currencyCode] = {
      base: price?.societe_reward_copay_base != null ? String(price.societe_reward_copay_base) : '',
      extraNight: price?.societe_reward_copay_extra_night != null ? String(price.societe_reward_copay_extra_night) : '',
    };
  }

  const overridden = parseOverriddenFields(reward.override_meta);
  // Rule 2b (self-healing): a package that's already overridden in ANY way (points/copay/legacy
  // eligibility) can never validly stay on Auto eligibility — see buildSocieteRewardAction's Rule
  // 2a gate. So even when eligibility itself isn't yet owned (a legacy override predating this
  // rule), seed the toggle from the current status rather than leaving it at Auto: the admin sees
  // their package's actual current eligibility, and — because the ownership-aware baseline in
  // buildSocieteRewardAction treats any non-null eligible as "changed" while unowned — resubmitting
  // this seeded value establishes eligibility ownership going forward without the admin having to
  // notice anything.
  const eligible = overridden.eligible || reward.is_manual_override ? isSocieteRewardEligible(reward.status) : null;

  return {
    pointsBase: String(reward.points_base),
    // A null rate (no nightly price) seeds an empty field — the "untouched" state — never "null".
    pointsExtraNight: reward.points_extra_night === null ? '' : String(reward.points_extra_night),
    copay,
    eligible,
    reason: '',
  };
}

/**
 * Builds the `override` admin action from the override dialog's form state, keeping only the
 * fields whose value actually differs from the package's current live value — an empty string
 * means that field was left untouched and is omitted entirely, so a partial override (e.g. points
 * only) never clobbers co-pay or eligibility it didn't mean to touch.
 *
 * Returns `{ error }` instead of throwing for every validation failure, so the dialog can render
 * the message inline (in an `Alert`) without wrapping every submit in a try/catch.
 *
 * Validation order (matches the error a user sees first):
 * 1. Nothing changed at all.
 * 2. Reason missing/blank (only once there's something to explain).
 * 3. A touched numeric field isn't a whole, non-negative number.
 * 4. A value field (points/co-pay) is being overridden while eligibility is still Auto and not
 *    yet admin-owned (Rule 2a — an overridden package can't be left auto-calculated).
 */
export function buildSocieteRewardAction(
  form: OverrideFormState,
  current: { reward: SocieteReward | null; prices: Array<SocieteRewardCopayPrice> },
): BuildSocieteRewardActionResult {
  // The override's `eligible` flag is compared against whether the reward is currently eligible
  // (per `isSocieteRewardEligible`); AUTO_INELIGIBLE and ADMIN_INELIGIBLE both count as "not
  // eligible" as a baseline.
  const liveEligible = current.reward ? isSocieteRewardEligible(current.reward.status) : null;
  // Rule 1 — ownership-aware baseline: whether eligibility is already admin-owned changes what
  // "changed" even means for this field.
  //  - OWNED: there's a real current override value to compare against (liveEligible, same as any
  //    other overridden field) — equal to it means "no edit", same as today.
  //  - NOT OWNED: eligibility is still formula-controlled, so there is no override value to diff
  //    against. Choosing ANY explicit eligibility — even one that happens to match today's
  //    computed status — IS the change: it moves eligibility from formula-owned to admin-owned.
  //    Comparing against the status value here would wrongly call an eligibility-only override on
  //    an already-AUTO_ELIGIBLE package "unchanged".
  const eligibilityOwned = current.reward ? parseOverriddenFields(current.reward.override_meta).eligible : false;
  const eligibleChanged = eligibilityOwned
    ? form.eligible !== null && form.eligible !== liveEligible
    : form.eligible !== null;

  const { pointsBaseChanged, pointsExtraNightChanged, copayEntries } = diffValueFields(form, current);

  const hasChanges = pointsBaseChanged || pointsExtraNightChanged || copayEntries.length > 0 || eligibleChanged;
  if (!hasChanges) {
    return { error: NOTHING_CHANGED_ERROR };
  }

  if (form.reason.trim() === '') {
    return { error: REASON_REQUIRED_ERROR };
  }

  const touchedNumericStrings = [
    ...(pointsBaseChanged ? [form.pointsBase] : []),
    ...(pointsExtraNightChanged ? [form.pointsExtraNight] : []),
    ...copayEntries.flatMap((entry) => {
      const values = form.copay[entry.currency_code];
      return [values.base, values.extraNight].filter((value) => value.trim() !== '');
    }),
  ];
  if (touchedNumericStrings.some((value) => !isWholeNonNegative(value))) {
    return { error: INVALID_NUMBER_ERROR };
  }

  // Rule 2a: once this action touches any value field, an unowned Auto eligibility is invalid —
  // the package is becoming (or staying) overridden and can no longer be left auto-calculated.
  // Doesn't apply once eligibility is already owned: that override stays in force untouched (the
  // field is simply omitted below), so there's nothing to "choose" this submit.
  const includesValueField = pointsBaseChanged || pointsExtraNightChanged || copayEntries.length > 0;
  if (includesValueField && !eligibilityOwned && form.eligible === null) {
    return { error: VALUES_REQUIRE_ELIGIBILITY_ERROR };
  }

  const action: OverrideAction = {
    action: 'override',
    reason: form.reason.trim(),
    ...(pointsBaseChanged ? { points_base: toNumber(form.pointsBase) } : {}),
    ...(pointsExtraNightChanged ? { points_extra_night: toNumber(form.pointsExtraNight) } : {}),
    ...(copayEntries.length > 0 ? { copay: copayEntries } : {}),
    ...(eligibleChanged ? { eligible: form.eligible as boolean } : {}),
  };

  return { action };
}
